/**
 * WorkspacePhaseTabs — the event workspace's internal wayfinding.
 *
 * Folder tabs (Quoting / Job Prep / Payments) over the three lifecycle stages;
 * each carries its progress state (active / done / future) so the operator sees
 * where the booking is at a glance. The bar sticks to the top of the viewport:
 * an open section (the gear editor especially) is far taller than a screen, and
 * without it the map scrolls away exactly when it is needed.
 *
 * Only the selected tab is drawn — an unselected tab with a filled background
 * reads as one the pointer is resting on (DESIGN-SYSTEM.md Interactive States).
 */
import { cx } from "@shared/cx";
import { FolderTab, FolderTabBar } from "../ui/FolderTabs";
import { CheckIcon } from "../ui/icons";
import srOnly from "@shared/a11y.module.css";
import styles from "./WorkspacePhaseTabs.module.css";

export type PhaseTabKey = "quoting" | "jobPrep" | "payments";

export interface PhaseTab {
  key: PhaseTabKey;
  label: string;
  isActive: boolean;
  isDone: boolean;
}

interface Props {
  tabs: PhaseTab[];
  current: PhaseTabKey;
  onSelect: (key: PhaseTabKey) => void;
}

export function WorkspacePhaseTabs({ tabs, current, onSelect }: Props) {
  return (
    <div className={styles.bar}>
      <FolderTabBar ariaLabel="Workspace phases">
        {tabs.map((tab) => {
          const selected = tab.key === current;
          return (
            <FolderTab
              key={tab.key}
              selected={selected}
              surface="page"
              onSelect={() => onSelect(tab.key)}
            >
              {tab.label}
              {tab.isDone && (
                <>
                  {/* The check is the phase's state, not a control: it reads out
                      as text rather than hiding behind a tooltip. */}
                  <CheckIcon
                    className={cx(styles.check, selected && styles.checkSelected)}
                    strokeWidth={2.5}
                    aria-hidden
                  />
                  <span className={srOnly.srOnly}>complete</span>
                </>
              )}
            </FolderTab>
          );
        })}
      </FolderTabBar>
    </div>
  );
}
